import { useToast } from 'components/ToastManager';
import { useCreateDataExportMutation } from 'hooks/internalApi';
import { useInternalApiContext } from 'hooks/useInternalApiContext';
import React, { useCallback, useState } from 'react';
import { getResultOrError } from 'utils/errors';
import { useWorkspaceId } from 'wrappers/WorkspaceProvider';

import { Icon } from '@fragment/ui/src/components/Icon';
import { MultiPartForm } from '@fragment/ui/src/components/MultiPartForm/MultiPartForm';
import { Toast } from '@fragment/ui/src/components/Toast';

import { NewDataExportFormAWSPage } from './NewDataExportFormAWSPage';
import { NewDataExportFormCreateExportPage } from './NewDataExportFormCreateExportPage';

type Props = {
  onSuccess: VoidFunction;
  onCancel: VoidFunction;
};

export const NewDataExportForm = ({ onSuccess, onCancel }: Props) => {
  const { showToast } = useToast();
  const [, createDataExport] = useCreateDataExportMutation();
  const { context } = useInternalApiContext();
  const workspaceId = useWorkspaceId();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (fieldValues: Record<string, string>) => {
      setErrorMessage(null);
      const { data, error } = await createDataExport(
        {
          workspaceId,
          dataExport: {
            id: fieldValues.exportName,
            exportBucketName: fieldValues.bucketName,
            exportBucketRegion: fieldValues.bucketRegion,
            exportBucketKey: fieldValues.filePrefix,
          },
        },
        context
      );
      const res = getResultOrError(data?.createDataExport, error);
      if (res.isError) {
        setErrorMessage(res.errorMessage);
      } else {
        showToast(
          <Toast
            message="Export Created. Data should arrive within 15 minutes."
            type="success"
          />
        );
        onSuccess();
      }
    },
    [createDataExport, workspaceId, context, showToast, onSuccess]
  );

  return (
    <div className="w-full max-w-[600px] space-y-f2">
      {errorMessage && (
        <div
          data-testid="create-data-export-error"
          className="flex items-start gap-f1"
        >
          <Icon type="error" backgroundColor="orange" />
          <span>{errorMessage}</span>
        </div>
      )}
      <MultiPartForm
        preventEventDefault
        pages={[NewDataExportFormAWSPage, NewDataExportFormCreateExportPage]}
        onCancel={onCancel}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises -- grandfathered
        onSubmit={handleSubmit}
      />
    </div>
  );
};
